import { Pie, PieChart, ResponsiveContainer, Sector, Tooltip } from "recharts";
import {
	REPORT_SUMMARY_CATEGORY_COLORS,
	REPORT_SUMMARY_CATEGORY_NAMES,
} from "../constants";
import type { DistributionByCategory } from "../types";
import { formatCurrency } from "../utils";

type DistributionTooltipPayload = {
	total: number;
	name: string;
	value: number;
	fill?: string;
};

type DistributionTooltipProps = {
	active?: boolean;
	payload?: Array<{ payload: DistributionTooltipPayload }>;
};

function DistributionTooltip({ active, payload }: DistributionTooltipProps) {
	if (!(active && payload?.length)) {
		return null;
	}
	const data = payload[0]?.payload as DistributionTooltipPayload;
	if (!data) {
		return null;
	}
	return (
		<div className="rounded-lg border border-[#E9EAEB] bg-white p-3 shadow-lg">
			<div className="flex items-center gap-2">
				<div
					className="size-3 shrink-0 rounded-full"
					style={{ backgroundColor: data.fill ?? "#6B7280" }}
				/>
				<span className="font-medium text-[#0A0D12]">{data.name}</span>
			</div>
			<p className="mt-1 text-[#535862] text-sm">
				{formatCurrency(data.total)} ({data.value.toFixed(0)}%)
			</p>
		</div>
	);
}

type ExpensesDistributionChartProps = {
	data: DistributionByCategory[];
};

export function ExpensesDistributionChart({
	data,
}: ExpensesDistributionChartProps) {
	const dataWithColors = data.map((item) => {
		const label = REPORT_SUMMARY_CATEGORY_NAMES[item.type];
		const color = REPORT_SUMMARY_CATEGORY_COLORS[item.type] ?? "#6B7280";

		return {
			name: label,
			value: item.percent,
			total: item.total,
			fill: color,
		};
	});

	return (
		<div className="rounded-xl border border-[#E9EAEB] bg-white p-5">
			<h3 className="mb-4 font-semibold text-[#0A0D12] text-lg">
				Distribuição por Categoria
			</h3>
			<div className="h-[220px] w-full">
				<ResponsiveContainer
					aspect={undefined}
					height={220}
					minHeight={0}
					minWidth={0}
					width="100%"
				>
					<PieChart>
						<Pie
							cx="50%"
							cy="50%"
							data={dataWithColors}
							dataKey="value"
							innerRadius={50}
							outerRadius={90}
							paddingAngle={0}
							shape={(props) => {
								const {
									cx,
									cy,
									innerRadius,
									outerRadius,
									startAngle,
									endAngle,
									cornerRadius,
									stroke,
									strokeWidth,
									index,
									payload,
								} = props;

								const pieFill = payload?.fill ? payload.fill : "#6B7280";

								return (
									<Sector
										cornerRadius={cornerRadius}
										cx={cx}
										cy={cy}
										endAngle={endAngle}
										fill={pieFill}
										innerRadius={innerRadius}
										key={payload?.name ?? index}
										outerRadius={outerRadius}
										startAngle={startAngle}
										stroke={stroke}
										strokeWidth={strokeWidth}
									/>
								);
							}}
							strokeWidth={2}
						/>
						<Tooltip content={<DistributionTooltip />} />
					</PieChart>
				</ResponsiveContainer>
			</div>
			<div className="mt-4 grid grid-cols-2 gap-x-3 gap-y-2">
				{dataWithColors.map((item) => (
					<div className="flex items-center gap-2" key={item.name}>
						<div
							className="size-3 shrink-0 rounded-full"
							style={{ backgroundColor: item.fill }}
						/>
						<span className="truncate text-[#535862] text-sm">{item.name}</span>
						<span className="ml-auto font-bold text-[#0A0D12] text-sm">
							{item.value?.toFixed(0)}%
						</span>
					</div>
				))}
			</div>
		</div>
	);
}
